import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Trophy, Plus, Trash2, Calendar, Users, Flag, Download } from "lucide-react";
import { api, formatApiError } from "../lib/api";

/**
 * Multi-day Ryder-Cup-style sessions UI. Renders only on golf events with
 * format=ryder_cup. Organizers add sessions (Day 1 - Foursomes etc.), build
 * pairings, and set 1-0 / 0-1 / ½-½ results. Cumulative cup score updates live.
 */
const SESSION_FORMATS = [
  { key: "foursomes", label: "Foursomes (alternate shot)" },
  { key: "four_ball", label: "Four-ball (best ball)" },
  { key: "singles", label: "Singles match play" },
];

export default function RyderCupRounds({ eventId, eventKind = "tournament", isOrganizer, registrants = [] }) {
  const [data, setData] = useState({ sub_rounds: [], team_a_total: 0, team_b_total: 0, matches_played: 0, matches_total: 0 });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newSession, setNewSession] = useState({ name: "Day 1 — Foursomes", date: "", session_format: "foursomes" });

  const load = useCallback(async () => {
    try {
      const { data: res } = await api.get(`/golf/sub-rounds?event_id=${eventId}`);
      setData(res);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  const createSession = async (e) => {
    e.preventDefault();
    if (!newSession.name) return;
    setCreating(true);
    try {
      await api.post("/golf/sub-rounds", { event_id: eventId, event_kind: eventKind, ...newSession });
      toast.success("Session added");
      setNewSession({ name: "", date: "", session_format: "foursomes" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setCreating(false); }
  };

  const deleteSession = async (srId) => {
    if (!window.confirm("Delete this session and all its pairings?")) return;
    try { await api.delete(`/golf/sub-rounds/${srId}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const [downloading, setDownloading] = useState(false);
  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/golf/sub-rounds/pdf?event_id=${eventId}&event_kind=${eventKind}`,
        { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Try to lift filename from Content-Disposition; fall back to a sane default
      const cd = res.headers?.["content-disposition"] || "";
      const match = /filename="?([^";]+)"?/i.exec(cd);
      a.download = match ? match[1] : "ryder-cup-cupboard.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Cup-board PDF downloaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message || "PDF generation failed");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return <div className="pp-card p-6 text-center text-zinc-500 text-sm">Loading sessions…</div>;

  return (
    <section className="space-y-4" data-testid="ryder-rounds">
      <CupScoreHeader data={data} />

      <div className="flex justify-end">
        <button
          onClick={downloadPdf}
          disabled={downloading}
          data-testid="ryder-download-pdf"
          title="Print-ready cup-board PDF for the venue"
          className="pp-btn-secondary px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 disabled:opacity-50"
        >
          <Download size={12} /> {downloading ? "Preparing…" : "Download PDF"}
        </button>
      </div>

      {data.sub_rounds.length === 0 && !isOrganizer && (
        <div className="pp-card p-8 text-center text-zinc-500 text-sm" data-testid="ryder-empty">
          <Trophy size={28} className="mx-auto mb-2 text-zinc-700" />
          The captains haven't set up sessions yet.
        </div>
      )}

      {data.sub_rounds.map((sr) => (
        <SubRoundCard key={sr.id} sr={sr} isOrganizer={isOrganizer} registrants={registrants} onChange={load} onDelete={deleteSession} />
      ))}

      {isOrganizer && (
        <form onSubmit={createSession} className="pp-card p-4 space-y-3" data-testid="ryder-add-session">
          <div className="flex items-center gap-2 text-[#00E676]">
            <Plus size={14} /> <span className="font-bold uppercase tracking-wider text-xs">Add session</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <input data-testid="ryder-name" placeholder="Session name (e.g. Day 1 - Foursomes)"
              value={newSession.name} onChange={(e) => setNewSession({ ...newSession, name: e.target.value })} className="pp-input" />
            <input data-testid="ryder-date" type="date" value={newSession.date}
              onChange={(e) => setNewSession({ ...newSession, date: e.target.value })} className="pp-input" />
            <select data-testid="ryder-format" value={newSession.session_format}
              onChange={(e) => setNewSession({ ...newSession, session_format: e.target.value })} className="pp-input">
              {SESSION_FORMATS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
          </div>
          <button type="submit" disabled={creating} data-testid="ryder-create"
            className="pp-btn-primary px-4 py-2 rounded-lg text-sm flex items-center gap-1 disabled:opacity-50">
            <Plus size={14} /> {creating ? "Adding…" : "Add session"}
          </button>
        </form>
      )}
    </section>
  );
}

function CupScoreHeader({ data }) {
  const a = data.team_a_total || 0;
  const b = data.team_b_total || 0;
  const aColor = a > b ? "#00E676" : a < b ? "#71717a" : "#FFD60A";
  const bColor = b > a ? "#00E676" : b < a ? "#71717a" : "#FFD60A";
  return (
    <div className="pp-card p-5 flex items-center justify-between gap-4" data-testid="ryder-cup-score">
      <div className="text-center flex-1">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500">Team A</div>
        <div className="text-5xl font-black mt-1" style={{ color: aColor }} data-testid="ryder-a-total">{a}</div>
      </div>
      <div className="text-center">
        <Trophy size={28} className="text-[#FFD60A] mx-auto" />
        <div className="text-[10px] text-zinc-500 mt-1">{data.matches_played}/{data.matches_total} done</div>
      </div>
      <div className="text-center flex-1">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500">Team B</div>
        <div className="text-5xl font-black mt-1" style={{ color: bColor }} data-testid="ryder-b-total">{b}</div>
      </div>
    </div>
  );
}

function SubRoundCard({ sr, isOrganizer, registrants, onChange, onDelete }) {
  const [adding, setAdding] = useState(false);
  return (
    <div className="pp-card p-4 space-y-3" data-testid={`ryder-sr-${sr.id}`}>
      <header className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Flag size={16} className="text-[#00E676]" />
          <span className="font-heading font-black">{sr.name}</span>
          {sr.date && <span className="text-xs text-zinc-500 flex items-center gap-1"><Calendar size={11} /> {sr.date}</span>}
          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/5 text-zinc-400 uppercase tracking-wider">{(sr.session_format || "").replace("_", " ")}</span>
        </div>
        {isOrganizer && (
          <div className="flex gap-1">
            <button onClick={() => setAdding((v) => !v)} data-testid={`ryder-add-pairing-${sr.id}`}
              className="pp-btn-secondary px-2 py-1 rounded-lg text-xs flex items-center gap-1"><Plus size={12} /> Pairing</button>
            <button onClick={() => onDelete(sr.id)} title="Delete session" data-testid={`ryder-del-sr-${sr.id}`}
              className="p-1.5 rounded-lg border border-[#FF3B30]/30 text-[#FF3B30] hover:bg-[#FF3B30]/10"><Trash2 size={12} /></button>
          </div>
        )}
      </header>

      {adding && (
        <PairingComposer sessionFormat={sr.session_format} subRoundId={sr.id} registrants={registrants} onDone={() => { setAdding(false); onChange(); }} />
      )}

      {sr.pairings.length === 0 ? (
        <div className="text-xs text-zinc-500 italic">No pairings yet.</div>
      ) : (
        <div className="space-y-1.5">
          {sr.pairings.map((p) => <PairingRow key={p.id} pairing={p} isOrganizer={isOrganizer} onChange={onChange} />)}
        </div>
      )}
    </div>
  );
}

function PairingComposer({ sessionFormat, subRoundId, registrants, onDone }) {
  const isSingles = sessionFormat === "singles";
  const playersPerSide = isSingles ? 1 : 2;
  const [a, setA] = useState(Array(playersPerSide).fill(""));
  const [b, setB] = useState(Array(playersPerSide).fill(""));

  const submit = async (e) => {
    e.preventDefault();
    const aFiltered = a.filter(Boolean);
    const bFiltered = b.filter(Boolean);
    if (aFiltered.length === 0 || bFiltered.length === 0) { toast.error("Pick at least one player on each side"); return; }
    try {
      await api.post("/golf/pairings", {
        sub_round_id: subRoundId,
        team_a_players: aFiltered,
        team_b_players: bFiltered,
      });
      toast.success("Pairing added");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <form onSubmit={submit} className="bg-white/[0.03] border border-white/5 p-3 rounded-lg space-y-2" data-testid={`pairing-form-${subRoundId}`}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{isSingles ? "Singles match" : "Pair vs Pair"}</div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <div className="text-xs text-zinc-400">Team A</div>
          {a.map((v, i) => <PlayerSelect key={`a-${subRoundId}-${i}`} value={v} onChange={(nv) => setA(a.map((x, j) => i === j ? nv : x))} registrants={registrants} testId={`pairing-a-${subRoundId}-${i}`} />)}
        </div>
        <div className="space-y-1.5">
          <div className="text-xs text-zinc-400">Team B</div>
          {b.map((v, i) => <PlayerSelect key={`b-${subRoundId}-${i}`} value={v} onChange={(nv) => setB(b.map((x, j) => i === j ? nv : x))} registrants={registrants} testId={`pairing-b-${subRoundId}-${i}`} />)}
        </div>
      </div>
      <button type="submit" className="pp-btn-primary px-3 py-1.5 rounded-lg text-xs">Save pairing</button>
    </form>
  );
}

function PlayerSelect({ value, onChange, registrants, testId }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className="pp-input text-xs py-1" data-testid={testId}>
      <option value="">— pick player —</option>
      {registrants.map((r) => (
        <option key={r.user_id || r.id} value={r.user_id || r.id}>{playerLabel(r)}</option>
      ))}
    </select>
  );
}

function playerLabel(r) {
  const u = r.user || r;
  return ((u.first_name || "") + " " + (u.last_name || "")).trim() || u.email || "Player";
}

function PairingRow({ pairing, isOrganizer, onChange }) {
  const setResult = async (a, b) => {
    try { await api.patch(`/golf/pairings/${pairing.id}`, { a_points: a, b_points: b }); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const del = async () => {
    if (!window.confirm("Delete pairing?")) return;
    try { await api.delete(`/golf/pairings/${pairing.id}`); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const aLead = pairing.a_points > pairing.b_points;
  const bLead = pairing.b_points > pairing.a_points;
  const tied = pairing.a_points === 0.5 && pairing.b_points === 0.5;

  return (
    <div className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-lg bg-white/[0.02] border border-white/5 text-sm" data-testid={`pairing-row-${pairing.id}`}>
      <div className={`flex-1 ${aLead ? "text-[#00E676] font-bold" : "text-zinc-300"}`}>
        <Users size={11} className="inline me-1" />
        {pairing.team_a.map((p) => p.name).join(" / ") || "—"}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {isOrganizer ? (
          <>
            <button data-testid={`p-${pairing.id}-a`} onClick={() => setResult(1, 0)}
              className={`pp-chip text-xs ${aLead ? "pp-chip-active" : ""}`}>1–0</button>
            <button data-testid={`p-${pairing.id}-tie`} onClick={() => setResult(0.5, 0.5)}
              className={`pp-chip text-xs ${tied ? "pp-chip-active" : ""}`}>½–½</button>
            <button data-testid={`p-${pairing.id}-b`} onClick={() => setResult(0, 1)}
              className={`pp-chip text-xs ${bLead ? "pp-chip-active" : ""}`}>0–1</button>
            <button data-testid={`p-${pairing.id}-clear`} onClick={() => setResult(0, 0)}
              title="Reset to pending" className="pp-chip text-xs text-zinc-500">Reset</button>
            <button onClick={del} data-testid={`p-${pairing.id}-del`} title="Delete"
              className="p-1 rounded border border-[#FF3B30]/30 text-[#FF3B30] hover:bg-[#FF3B30]/10"><Trash2 size={10} /></button>
          </>
        ) : (
          <span className="text-xs text-zinc-400">{pairing.a_points}–{pairing.b_points}</span>
        )}
      </div>
      <div className={`flex-1 text-right ${bLead ? "text-[#00E676] font-bold" : "text-zinc-300"}`}>
        {pairing.team_b.map((p) => p.name).join(" / ") || "—"}
        <Users size={11} className="inline ms-1" />
      </div>
    </div>
  );
}
